import React, { useState, useEffect, useRef } from 'react'
import { Bell, Send, Calendar, AlertCircle, Info, CheckCircle2, AlertTriangle, Wrench, X, Plus } from 'lucide-react'
import type { PlatformNotification } from '../lib/storage'

interface NotificationComposerProps {
  tenantList: Array<{ id: string; name: string }> // List of all tenants
  currentUserEmail: string
  onClose?: () => void
}

const NotificationComposer: React.FC<NotificationComposerProps> = ({
  tenantList,
  currentUserEmail,
  onClose
}) => {
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [type, setType] = useState<'info' | 'warning' | 'success' | 'update' | 'maintenance'>('info')
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium')
  const [targetTenants, setTargetTenants] = useState<string[] | 'all'>('all')
  const [selectedTenants, setSelectedTenants] = useState<string[]>([])
  const [scheduledFor, setScheduledFor] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [actionLabel, setActionLabel] = useState('')
  const [actionUrl, setActionUrl] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showHistory, setShowHistory] = useState(false)
  const [history, setHistory] = useState<PlatformNotification[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (showHistory) {
      fetchHistory()
    }
  }, [showHistory])

  const fetchHistory = async () => {
    setLoadingHistory(true)
    try {
      const response = await fetch('/api/admin/notifications')
      if (!response.ok) throw new Error('Failed to fetch notification history')
      const data = await response.json()
      setHistory(data.notifications || [])
    } catch (err: any) {
      console.error('[NotificationComposer] Failed to fetch history:', err)
      setError(err instanceof Error ? err.message : 'Failed to load notification history')
    } finally {
      setLoadingHistory(false)
    }
  }

  const handleSend = async () => {
    setError(null)
    setSuccess(null)

    if (!title.trim() || !message.trim()) {
      setError('Title and message are required')
      return
    }

    setSending(true)

    try {
      const response = await fetch('/api/notifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          message,
          type,
          priority,
          targetTenants: targetTenants === 'all' ? 'all' : selectedTenants,
          scheduledFor: scheduledFor || undefined,
          expiresAt: expiresAt || undefined,
          actionLabel: actionLabel || undefined,
          actionUrl: actionUrl || undefined,
          createdBy: currentUserEmail
        })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to send notification')
      }

      setSuccess(scheduledFor ? 'Notification scheduled successfully' : 'Notification sent successfully')

      // Reset form
      setTitle('')
      setMessage('')
      setType('info')
      setPriority('medium')
      setTargetTenants('all')
      setSelectedTenants([])
      setScheduledFor('')
      setExpiresAt('')
      setActionLabel('')
      setActionUrl('')

      // Refresh history if visible
      if (showHistory) {
        fetchHistory()
      }

      // Clear success message after 3 seconds
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current)
      }
      successTimeoutRef.current = setTimeout(() => {
        setSuccess(null)
        successTimeoutRef.current = null
      }, 3000)

    } catch (err: any) {
      console.error('[NotificationComposer] Failed to send notification:', err)
      setError(err instanceof Error ? err.message : 'Failed to send notification')
    } finally {
      setSending(false)
    }
  }

  const toggleTenant = (tenantId: string) => {
    setSelectedTenants(prev =>
      prev.includes(tenantId)
        ? prev.filter(id => id !== tenantId)
        : [...prev, tenantId]
    )
  }

  const typeIcons = {
    info: <Info className="h-5 w-5 text-blue-600" />,
    warning: <AlertTriangle className="h-5 w-5 text-yellow-600" />,
    success: <CheckCircle2 className="h-5 w-5 text-green-600" />,
    update: <Bell className="h-5 w-5 text-purple-600" />,
    maintenance: <Wrench className="h-5 w-5 text-orange-600" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="h-6 w-6 text-primary-600" />
          <h2 className="text-2xl font-bold text-gray-900">Platform Notifications</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {showHistory ? 'Hide History' : 'View History'}
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          )}
        </div>
      </div>

      <p className="text-gray-600">
        Send notifications to all tenants or specific tenants. Notifications appear under the bell icon in each tenant's dashboard.
      </p>

      {/* Status Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-red-900">Error</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-green-900">Success</p>
            <p className="text-sm text-green-700">{success}</p>
          </div>
        </div>
      )}

      {/* Notification History */}
      {showHistory && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification History</h3>
          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-gray-600 text-center py-8">No notifications sent yet</p>
          ) : (
            <div className="space-y-3">
              {history.map(notif => (
                <div key={notif.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    {typeIcons[notif.type]}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="font-semibold text-gray-900">{notif.title}</h4>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          notif.priority === 'high' ? 'bg-red-100 text-red-700' :
                          notif.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {notif.priority}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{notif.message}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>
                          {notif.targetTenants === 'all' ? 'All tenants' : `${notif.targetTenants.length} tenant(s)`}
                        </span>
                        <span>•</span>
                        <span>
                          {/* Safety improvement: Use optional chaining instead of non-null assertions to prevent runtime errors */}
                          {notif.status === 'sent' && notif.sentAt ? `Sent ${new Date(notif.sentAt).toLocaleDateString()}` :
                           notif.status === 'scheduled' && notif.scheduledFor ? `Scheduled for ${new Date(notif.scheduledFor).toLocaleDateString()}` :
                           'Draft'}
                        </span>
                        <span>•</span>
                        <span>By {notif.createdBy}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Composer Form */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-6">
        <h3 className="text-lg font-semibold text-gray-900">Compose Notification</h3>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., System Maintenance Scheduled"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter your notification message..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Type and Priority */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="info">Info</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="update">Update</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>
        </div>

        {/* Target Tenants */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Target Tenants</label>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={targetTenants === 'all'}
                onChange={() => setTargetTenants('all')}
                className="h-4 w-4 text-blue-600"
              />
              <span className="text-sm text-gray-900">All Tenants (Broadcast)</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={targetTenants !== 'all'}
                onChange={() => setTargetTenants([])}
                className="h-4 w-4 text-blue-600"
              />
              <span className="text-sm text-gray-900">Specific Tenants</span>
            </label>
          </div>

          {targetTenants !== 'all' && (
            <div className="mt-3 border border-gray-200 rounded-lg p-4 max-h-48 overflow-y-auto">
              {tenantList.length === 0 ? (
                <p className="text-sm text-gray-600">No tenants available</p>
              ) : (
                <div className="space-y-2">
                  {tenantList.map(tenant => (
                    <label key={tenant.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedTenants.includes(tenant.id)}
                        onChange={() => toggleTenant(tenant.id)}
                        className="h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="text-sm text-gray-900">{tenant.name || tenant.id}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Optional: Scheduling */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Schedule For <span className="text-gray-500 font-normal">(optional)</span>
            </label>
            <input
              type="datetime-local"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Expires At <span className="text-gray-500 font-normal">(optional)</span>
            </label>
            <input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Optional: Action Button */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Action Button Label <span className="text-gray-500 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={actionLabel}
              onChange={(e) => setActionLabel(e.target.value)}
              placeholder="e.g., Learn More"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Action URL <span className="text-gray-500 font-normal">(optional)</span>
            </label>
            <input
              type="url"
              value={actionUrl}
              onChange={(e) => setActionUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Send Button */}
        <div className="flex items-center justify-end pt-4 border-t border-gray-200">
          <button
            onClick={handleSend}
            disabled={sending || !title.trim() || !message.trim()}
            className="px-6 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-md hover:from-primary-700 hover:to-primary-800 font-medium transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {sending ? (
              <>
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                Sending...
              </>
            ) : scheduledFor ? (
              <>
                <Calendar className="h-4 w-4" />
                Schedule Notification
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Send Notification
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default NotificationComposer
